/**
 * Connexion Google côté navigateur
 *
 * Trois choses vivent ici, et aucune ne mérite d'être dans l'écran de connexion :
 * le chargement du script de Google, le pont vers l'application Android, et la
 * détection du mode « connexion mobile ».
 *
 * ── Pourquoi un détour par le navigateur sur Android ──
 *
 * Google refuse ses pages de connexion dans une WebView embarquée — « disallowed
 * user agent ». C'est délibéré de leur part : une application qui affiche un
 * formulaire Google dans sa propre WebView peut en lire le contenu. L'app ouvre
 * donc le SITE dans un onglet de navigateur système, s'y connecte normalement,
 * et le site lui renvoie le jeton de session par un lien profond
 * (`ovh.mainedufour.foyer://auth?jeton=…`).
 *
 * C'est le mécanisme retenu dans declaration-sterne, repris tel quel : il évite
 * d'embarquer un SDK natif Google Sign-In, donc une empreinte SHA-1 de plus à
 * tenir à jour dans la console Google à chaque rotation de clé de signature.
 *
 * Le jeton transite par l'URL du lien profond. C'est acceptable parce que le
 * schéma est propre à l'application et que le lien ne sort jamais de l'appareil
 * — mais c'est bien un jeton en clair dans une URL, donc il ne doit jamais être
 * journalisé.
 */

import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

import { BASE } from './api.js';

/** Identifiant client Google, côté navigateur. Vide = bouton masqué. */
export const CLIENT_ID_GOOGLE = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();

export const EST_NATIF = Capacitor.isNativePlatform();

/** Schéma de lien profond par lequel le site rend la main à l'application. */
export const LIEN_APPLI = 'ovh.mainedufour.foyer://auth';

/**
 * Le site est-il ouvert par l'application pour la seule connexion ?
 *
 * Dans ce mode, on ne fait PAS entrer l'utilisateur dans le site : on récupère
 * le jeton et on repart vers l'application. Entrer ici laisserait deux sessions
 * ouvertes — une dans le navigateur, une dans l'app — pour un seul geste.
 */
export function estModeConnexionMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('connexion_mobile');
}

/** Ouvre la connexion du site dans le navigateur système. */
export async function ouvreConnexionNavigateur(): Promise<void> {
  // `BASE` est l'origine publique, déjà nécessaire pour joindre l'API depuis la
  // WebView : la réutiliser évite une seconde URL en dur qui finirait par diverger.
  await Browser.open({ url: `${BASE}/?connexion_mobile=1`, presentationStyle: 'popover' });
}

/** Rend la main à l'application avec le jeton de session. */
export function rendsLaMainAAppli(jeton: string): void {
  window.location.href = `${LIEN_APPLI}?jeton=${encodeURIComponent(jeton)}`;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(options: { client_id: string; callback: (r: { credential: string }) => void }): void;
          renderButton(parent: HTMLElement, options: Record<string, string>): void;
        };
      };
    };
  }
}

let chargement: Promise<void> | null = null;

/**
 * Charge le script Google Identity Services, une seule fois.
 *
 * La promesse est mémorisée : deux rendus de l'écran de connexion — ce que React
 * fait systématiquement en mode strict — insèreraient sinon deux balises et
 * initialiseraient le client deux fois.
 */
export function chargeGoogle(): Promise<void> {
  if (window.google) return Promise.resolve();
  chargement ??= new Promise<void>((resous, rejette) => {
    const balise = document.createElement('script');
    balise.src = 'https://accounts.google.com/gsi/client';
    balise.async = true;
    balise.defer = true;
    balise.onload = () => resous();
    balise.onerror = () => {
      // Remis à zéro pour qu'un second essai puisse retenter : hors ligne au
      // premier chargement, la promesse resterait sinon rejetée pour toujours.
      chargement = null;
      rejette(new Error('Le script de connexion Google n’a pas pu être chargé.'));
    };
    document.head.appendChild(balise);
  });
  return chargement;
}
